import { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, Loader2, Check, X, Package, ChefHat, Wine, AlertTriangle, Image as ImageIcon, Plus, Send } from 'lucide-react';
import { api, formatCurrency } from '../../lib/api';
import { toast } from 'sonner';

// Compress image for iOS (photos can be 5-15MB)
export const compressImage = (file) => {
  return new Promise((resolve) => {
    if (!file || !file.type.startsWith('image/')) {
      resolve(file);
      return;
    }
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const maxSize = 1200;
            let { width, height } = img;
            if (width > maxSize || height > maxSize) {
              if (width > height) {
                height = (height / width) * maxSize;
                width = maxSize;
              } else {
                width = (width / height) * maxSize;
                height = maxSize;
              }
            }
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob((blob) => {
              if (blob && blob.size < file.size) {
                resolve(new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }));
              } else {
                resolve(file);
              }
            }, 'image/jpeg', 0.6);
          } catch {
            resolve(file);
          }
        };
        img.onerror = () => resolve(file);
        img.src = e.target.result;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    } catch {
      resolve(file);
    }
  });
};

// iOS Safari bug (iOS 17+): onChange/onInput don't fire from Photos app.
// Multi-layer workaround:
// 1. Native change/input event listeners
// 2. visibilitychange detection (when picker closes, page regains focus)
// 3. window focus detection
// 4. Polling fallback: after click, poll for files every 500ms
export const useFileInput = (onFileSelected) => {
  const ref = useRef(null);
  const handlerRef = useRef(onFileSelected);
  const processedRef = useRef(false);
  const pollingRef = useRef(null);

  // Always keep the latest handler
  useEffect(() => { handlerRef.current = onFileSelected; }, [onFileSelected]);

  const processFiles = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const files = el.files;
    if (!files || files.length === 0) return;
    if (processedRef.current) return;
    processedRef.current = true;
    // Stop polling
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    // Process all files
    for (let i = 0; i < files.length; i++) {
      handlerRef.current(files[i]);
    }
    // Reset immediately so next photo works right away
    el.value = '';
    processedRef.current = false;
  }, []);

  // Start polling when input is tapped
  const startPolling = useCallback(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    const start = Date.now();
    pollingRef.current = setInterval(() => {
      // Stop after 120 seconds
      if (Date.now() - start > 120000) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        return;
      }
      processFiles();
    }, 500);
  }, [processFiles]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Method 1: Native change event
    const onNativeChange = () => processFiles();
    // Method 2: Native input event
    const onNativeInput = () => processFiles();
    // Method 3: Start polling on click/touchend
    const onTap = () => startPolling();

    el.addEventListener('change', onNativeChange, false);
    el.addEventListener('input', onNativeInput, false);
    el.addEventListener('click', onTap, false);

    // Method 4: visibilitychange — page regains focus after picker closes
    const onVisibility = () => {
      if (!document.hidden && el.files?.length > 0) {
        setTimeout(() => processFiles(), 300);
      }
    };
    document.addEventListener('visibilitychange', onVisibility, false);

    // Method 5: window focus
    const onFocus = () => {
      if (el.files?.length > 0) {
        setTimeout(() => processFiles(), 300);
      }
    };
    window.addEventListener('focus', onFocus, false);

    return () => {
      el.removeEventListener('change', onNativeChange, false);
      el.removeEventListener('input', onNativeInput, false);
      el.removeEventListener('click', onTap, false);
      document.removeEventListener('visibilitychange', onVisibility, false);
      window.removeEventListener('focus', onFocus, false);
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [processFiles, startPolling]);

  return ref;
};


export const FileUpload = ({ 
  onUpload, 
  endpoint = '/analyze-invoice',
  source = 'patron',
  accept = "image/*,application/pdf",
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [status, setStatus] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [previews, setPreviews] = useState([]);

  const addFile = useCallback(async (file) => {
    try {
      const compressed = await compressImage(file);
      setPendingFiles(prev => [...prev, compressed]);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviews(prev => [...prev, e.target.result]);
      };
      reader.readAsDataURL(compressed);
    } catch (err) {
      toast.error('Erreur compression image');
    }
  }, []);

  const removeFile = useCallback((index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearAll = useCallback(() => {
    setPendingFiles([]);
    setPreviews([]);
  }, []);

  const sendFiles = useCallback(async () => {
    if (pendingFiles.length === 0) return;
    setIsUploading(true);
    setStatus('Compression des images...');
    try {
      // Compress all images to base64 on client side to avoid large multipart uploads
      const compressFile = async (file) => {
        return new Promise((resolve) => {
          if (file.type === 'application/pdf') {
            // For PDFs, send as-is but compressed
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.readAsDataURL(file);
          } else {
            // For images, resize and compress
            const img = new Image();
            img.onload = () => {
              const canvas = document.createElement('canvas');
              const maxDim = 1400;
              let w = img.width, h = img.height;
              if (w > maxDim || h > maxDim) {
                const ratio = Math.min(maxDim / w, maxDim / h);
                w = Math.round(w * ratio);
                h = Math.round(h * ratio);
              }
              canvas.width = w;
              canvas.height = h;
              canvas.getContext('2d').drawImage(img, 0, 0, w, h);
              const dataUrl = canvas.toDataURL('image/jpeg', 0.65);
              resolve(dataUrl.split(',')[1]);
            };
            img.src = URL.createObjectURL(file);
          }
        });
      };

      if (pendingFiles.length === 1 && !pendingFiles[0].type.includes('pdf')) {
        // Single image: use multipart (fast)
        const formData = new FormData();
        formData.append('file', pendingFiles[0]);
        setStatus('Analyse IA en cours...');
        const response = await api.post(`${endpoint}?source=${source}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });
        toast.success('Facture analysee');
        if (onUpload) onUpload(response.data);
      } else {
        // Multiple files or PDF: compress to base64 and send as JSON (bypasses proxy upload limits)
        setStatus(`Compression (${pendingFiles.length} fichiers)...`);
        const images = [];
        for (let i = 0; i < pendingFiles.length; i++) {
          setStatus(`Compression ${i + 1}/${pendingFiles.length}...`);
          const b64 = await compressFile(pendingFiles[i]);
          images.push({ data: b64, type: pendingFiles[i].type, name: pendingFiles[i].name });
        }
        setStatus(`Analyse IA (${images.length} pages)...`);
        const response = await api.post(`${endpoint}-base64?source=${source}`, { images }, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 180000,
        });
        toast.success(`Facture analysee (${pendingFiles.length} pages)`);
        if (onUpload) onUpload(response.data);
      }
      clearAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'analyse");
    } finally {
      setIsUploading(false);
      setStatus('');
    }
  }, [pendingFiles, endpoint, source, onUpload, clearAll]);

  // Two persistent hidden inputs: camera (single) + gallery (multiple)
  const cameraRef = useFileInput(addFile);
  const galleryRef = useFileInput(addFile);

  const triggerCamera = useCallback(() => {
    if (cameraRef.current) { cameraRef.current.value = ''; cameraRef.current.click(); }
  }, [cameraRef]);

  const triggerGallery = useCallback(() => {
    if (galleryRef.current) { galleryRef.current.value = ''; galleryRef.current.click(); }
  }, [galleryRef]);

  return (
    <div className="relative" data-testid="file-upload-zone">
      {/* Persistent hidden inputs — camera single + gallery multiple */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="sr-only" style={{ position: 'fixed', top: '-9999px', left: '-9999px' }} data-testid="file-upload-camera" />
      <input ref={galleryRef} type="file" accept={accept} multiple className="sr-only" style={{ position: 'fixed', top: '-9999px', left: '-9999px' }} data-testid="file-upload-gallery" />

      {isUploading ? (
        <div className="border-2 border-dashed rounded-xl p-8 text-center border-[rgb(var(--lm-gold))/0.5]">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-12 h-12 animate-spin text-[rgb(var(--lm-gold))]" />
            <p className="text-[rgb(var(--lm-text-secondary))]">{status}</p>
          </div>
        </div>
      ) : pendingFiles.length > 0 ? (
        <div className="border-2 border-dashed rounded-xl p-4 border-[rgb(var(--lm-gold))/0.5] space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-[rgb(var(--lm-gold))]">
              {pendingFiles.length} page{pendingFiles.length > 1 ? 's' : ''} prete{pendingFiles.length > 1 ? 's' : ''}
            </span>
            <button onClick={clearAll} className="text-xs text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]" data-testid="clear-pages-btn">
              Tout effacer
            </button>
          </div>
          <div className="flex gap-2 justify-center flex-wrap">
            {previews.map((src, i) => (
              <div key={i} className="relative w-16 h-20 rounded-lg overflow-hidden border border-[rgb(var(--lm-border))]">
                <img src={src} alt={`Page ${i + 1}`} className="w-full h-full object-cover" />
                <button onClick={() => removeFile(i)} className="absolute top-0 right-0 bg-black/60 rounded-bl-lg p-0.5">
                  <X size={10} className="text-white" />
                </button>
                <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[8px] text-center py-0.5">P{i + 1}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={triggerCamera} className="py-2.5 rounded-lg bg-[rgb(var(--lm-surface-elevated))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] text-xs font-medium flex items-center justify-center gap-1.5 cursor-pointer" data-testid="add-camera-btn">
              <Camera size={14} /> + Photo
            </button>
            <button onClick={triggerGallery} className="py-2.5 rounded-lg bg-[rgb(var(--lm-surface-elevated))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] text-xs font-medium flex items-center justify-center gap-1.5 cursor-pointer" data-testid="add-gallery-btn">
              <ImageIcon size={14} /> + Galerie
            </button>
          </div>
          <button onClick={sendFiles} className="w-full py-3 rounded-xl bg-[rgb(var(--lm-gold))] text-black font-medium flex items-center justify-center gap-2 hover:opacity-90" data-testid="send-pages-btn">
            <Send size={16} />
            Envoyer {pendingFiles.length} page{pendingFiles.length > 1 ? 's' : ''}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <button onClick={triggerCamera} className="py-6 rounded-xl bg-[rgb(var(--lm-gold))] text-black font-medium flex flex-col items-center justify-center gap-2 cursor-pointer hover:opacity-90 transition-all" data-testid="scan-camera-btn">
            <Camera className="w-7 h-7" />
            <span className="text-sm">Scanner</span>
            <span className="text-[10px] opacity-70">1 photo</span>
          </button>
          <button onClick={triggerGallery} className="py-6 rounded-xl bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text))] font-medium flex flex-col items-center justify-center gap-2 border border-[rgb(var(--lm-border))] cursor-pointer hover:bg-[rgb(var(--lm-surface))] transition-all" data-testid="scan-gallery-btn">
            <ImageIcon className="w-7 h-7" />
            <span className="text-sm">Galerie / PDF</span>
            <span className="text-[10px] opacity-50">Multi-pages</span>
          </button>
        </div>
      )}
    </div>
  );
};

const ScanResult = ({ data, onClose }) => {
  if (!data) return null;
  const nbProduits = data.produits?.length || 0;
  return (
    <div className="lm-card p-4 border-[rgb(var(--lm-green))/0.3] bg-[rgb(var(--lm-green))/0.03] animate-slide-up" data-testid="scan-result">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Check size={18} className="text-[rgb(var(--lm-green))]" />
          <span className="text-xs font-medium text-[rgb(var(--lm-green))]">Facture scannee</span>
        </div>
        <button onClick={onClose} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]"><X size={14} /></button>
      </div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="font-medium text-[rgb(var(--lm-text))]">{data.fournisseur}</p>
          <p className="text-xs text-[rgb(var(--lm-text-muted))]">{data.date}</p>
        </div>
        <p className="font-mono text-xl text-[rgb(var(--lm-green))]">{formatCurrency(data.total_ttc)}</p>
      </div>
      <div className="flex gap-3 mb-3 text-xs">
        {(data.cuisine_ht > 0) && <div className="flex items-center gap-1"><ChefHat size={12} style={{ color: 'rgb(var(--lm-green))' }} /><span className="font-mono">{formatCurrency(data.cuisine_ht)}</span></div>}
        {(data.boissons_ht > 0) && <div className="flex items-center gap-1"><Wine size={12} style={{ color: 'rgb(100, 149, 237)' }} /><span className="font-mono">{formatCurrency(data.boissons_ht)}</span></div>}
        {(data.non_alimentaire_ht > 0) && <div className="flex items-center gap-1"><Package size={12} style={{ color: 'rgb(var(--lm-orange))' }} /><span className="font-mono">{formatCurrency(data.non_alimentaire_ht)}</span></div>}
      </div>
      {nbProduits > 0 ? (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))] mb-1.5">{nbProduits} produits extraits</p>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {data.produits.slice(0, 10).map((p, i) => (
              <div key={i} className="flex justify-between text-xs bg-[rgb(var(--lm-bg))/0.5] rounded px-2 py-1.5">
                <span className="truncate flex-1">{p.nom}</span>
                <span className="font-mono shrink-0 ml-2">{p.quantite} {p.unite} — {formatCurrency(p.montant_ht)}</span>
              </div>
            ))}
            {nbProduits > 10 && <p className="text-[10px] text-[rgb(var(--lm-text-muted))] text-center">+{nbProduits - 10} autres</p>}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-xs text-amber-400 bg-amber-500/5 rounded p-2">
          <AlertTriangle size={12} />
          <span>Aucun produit extrait — re-scannez</span>
        </div>
      )}
    </div>
  );
};


export const QuickUploadButton = ({ 
  onUpload, 
  endpoint = '/analyze-invoice',
  source = 'chef',
  variant = 'green',
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [status, setStatus] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  
  const bgColor = variant === 'green' ? 'bg-[rgb(var(--lm-green))]' : 'bg-[rgb(var(--lm-gold))]';

  const addFile = useCallback(async (file) => {
    const compressed = await compressImage(file);
    setPendingFiles(prev => [...prev, compressed]);
    // Create thumbnail preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviews(prev => [...prev, e.target.result]);
    };
    reader.readAsDataURL(compressed);
    toast.success(`Page ${pendingFiles.length + 1} ajoutee`);
  }, [pendingFiles.length]);

  const removeFile = useCallback((index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearAll = useCallback(() => {
    setPendingFiles([]);
    setPreviews([]);
  }, []);
  
  const sendFiles = useCallback(async () => {
    if (pendingFiles.length === 0) return;
    setIsUploading(true);
    setScanResult(null);
    try {
      // Helper to compress file to base64
      const compressToB64 = (file) => new Promise((resolve) => {
        if (file.type === 'application/pdf') {
          const reader = new FileReader();
          reader.onload = () => resolve({ data: reader.result.split(',')[1], type: file.type, name: file.name });
          reader.readAsDataURL(file);
        } else {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const maxDim = 1400;
            let w = img.width, h = img.height;
            if (w > maxDim || h > maxDim) { const r = Math.min(maxDim/w, maxDim/h); w = Math.round(w*r); h = Math.round(h*r); }
            canvas.width = w; canvas.height = h;
            canvas.getContext('2d').drawImage(img, 0, 0, w, h);
            resolve({ data: canvas.toDataURL('image/jpeg', 0.65).split(',')[1], type: 'image/jpeg', name: file.name });
          };
          img.src = URL.createObjectURL(file);
        }
      });

      if (pendingFiles.length === 1 && !pendingFiles[0].type.includes('pdf')) {
        const formData = new FormData();
        formData.append('file', pendingFiles[0]);
        setStatus('Analyse IA en cours...');
        const response = await api.post(`${endpoint}?source=${source}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });
        const data = response.data;
        toast.success(`${data.fournisseur} — ${data.produits?.length || 0} produit(s)`);
        setScanResult(data);
        if (onUpload) onUpload(data);
      } else {
        setStatus(`Compression (${pendingFiles.length} fichiers)...`);
        const images = [];
        for (let i = 0; i < pendingFiles.length; i++) {
          setStatus(`Compression ${i+1}/${pendingFiles.length}...`);
          images.push(await compressToB64(pendingFiles[i]));
        }
        setStatus(`Analyse IA (${images.length} pages)...`);
        const response = await api.post(`${endpoint}-base64?source=${source}`, { images }, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 180000,
        });
        const data = response.data;
        toast.success(`${data.fournisseur} — ${data.produits?.length || 0} produit(s) (${pendingFiles.length} pages)`);
        setScanResult(data);
        if (onUpload) onUpload(data);
      }
      clearAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'analyse");
    } finally {
      setIsUploading(false);
      setStatus('');
    }
  }, [pendingFiles, endpoint, source, onUpload, clearAll]);

  const cameraRef = useFileInput(addFile);
  const galleryRef = useFileInput(addFile);

  const triggerCamera = useCallback(() => {
    if (cameraRef.current) { cameraRef.current.value = ''; cameraRef.current.click(); }
  }, [cameraRef]);

  const triggerGallery = useCallback(() => {
    if (galleryRef.current) { galleryRef.current.value = ''; galleryRef.current.click(); }
  }, [galleryRef]);
  
  return (
    <div className="space-y-3">
      {/* Persistent hidden inputs — always mounted, iOS listeners always active */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="sr-only" style={{ position: 'fixed', top: '-9999px', left: '-9999px' }} data-testid="scan-camera-input" />
      <input ref={galleryRef} type="file" accept="image/*,application/pdf" multiple className="sr-only" style={{ position: 'fixed', top: '-9999px', left: '-9999px' }} data-testid="scan-gallery-input" />

      {isUploading ? (
        <div className="w-full py-8 rounded-xl bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] flex flex-col items-center justify-center gap-3" data-testid="scan-loading">
          <Loader2 className="w-10 h-10 animate-spin text-[rgb(var(--lm-green))]" />
          <p className="text-sm text-[rgb(var(--lm-text-secondary))]">{status}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2" data-testid="scan-buttons">
            <button onClick={triggerCamera} className={`py-5 rounded-xl ${bgColor} text-white font-medium flex flex-col items-center justify-center gap-2 cursor-pointer`} data-testid="scan-camera-btn">
              <Camera className="w-7 h-7" />
              <span className="text-sm">Scanner</span>
            </button>
            
            <button onClick={triggerGallery} className="py-5 rounded-xl bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text))] font-medium flex flex-col items-center justify-center gap-2 border border-[rgb(var(--lm-border))] cursor-pointer" data-testid="scan-gallery-btn">
              <ImageIcon className="w-7 h-7" />
              <span className="text-sm">Galerie / PDF</span>
            </button>
          </div>

          {/* Pending pages preview */}
          {pendingFiles.length > 0 && (
            <div className="lm-card p-3 border-[rgb(var(--lm-gold))/0.3] animate-slide-up" data-testid="pending-pages">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-[rgb(var(--lm-gold))]">
                  {pendingFiles.length} page{pendingFiles.length > 1 ? 's' : ''} en attente
                </span>
                <button onClick={clearAll} className="text-xs text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]" data-testid="clear-pages-btn">
                  Tout effacer
                </button>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {previews.map((src, i) => (
                  <div key={i} className="relative shrink-0 w-16 h-20 rounded-lg overflow-hidden border border-[rgb(var(--lm-border))]">
                    <img src={src} alt={`Page ${i + 1}`} className="w-full h-full object-cover" />
                    <button onClick={() => removeFile(i)} className="absolute top-0 right-0 bg-black/60 rounded-bl-lg p-0.5" data-testid={`remove-page-${i}`}>
                      <X size={10} className="text-white" />
                    </button>
                    <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[8px] text-center py-0.5">
                      P{i + 1}
                    </span>
                  </div>
                ))}
                {/* Add more button */}
                <button onClick={triggerCamera} className="shrink-0 w-16 h-20 rounded-lg border-2 border-dashed border-[rgb(var(--lm-gold))/0.4] flex flex-col items-center justify-center cursor-pointer" data-testid="add-more-page-btn">
                  <Plus size={16} className="text-[rgb(var(--lm-gold))]" />
                  <span className="text-[8px] text-[rgb(var(--lm-gold))]">Page</span>
                </button>
              </div>
              {/* Send button */}
              <button
                onClick={sendFiles}
                className={`w-full mt-2 py-3 rounded-xl ${bgColor} text-white font-medium flex items-center justify-center gap-2 transition-all hover:opacity-90`}
                data-testid="send-pages-btn"
              >
                <Send size={16} />
                Envoyer {pendingFiles.length} page{pendingFiles.length > 1 ? 's' : ''}
              </button>
            </div>
          )}
        </>
      )}

      <ScanResult data={scanResult} onClose={() => setScanResult(null)} />
    </div>
  );
};
